import { AppHelper } from '../../../../helpers/app.helper';
import { Range } from '../../entities/range.model';
import { Table } from '../../entities/table.model';
import { DropDownItem } from '../../../fields/dropdown-item.model';


export class MdmSearchDropDownItemsTable extends Table<{ [key: string]: DropDownItem[]; }> {
  private readonly taxonomyFieldItemIdsCache: { [key: string]: string[]; } = {};

  constructor(
    private appHelper: AppHelper,
    private permanentColumnsCount: number,
    range: Range) {
    super(range, false, {});
  }

  add(taxonomyId: number, fieldId: number, itemId: string, items: DropDownItem[]): boolean {
    if (!this.exists(taxonomyId, fieldId, itemId)) {
      const columnDiff = items.length - (this.range.columnCount - this.permanentColumnsCount);

      if (columnDiff > 0) {
        this.range.columnCount += columnDiff;
      }

      if (this.containsData) {
        this.range.rowCount++;
      } else {
        this.containsData = true;
      }

      this.cache[this.getItemKey(taxonomyId, fieldId, itemId)] = items;
      this.addTaxonomyFieldItemId(taxonomyId, fieldId, itemId);

      return true;
    }

    return false;
  }

  remove(taxonomyId: number, fieldId: number, itemId: string): boolean {
    if (this.exists(taxonomyId, fieldId, itemId)) {
      this.removeTaxonomyFieldItemId(taxonomyId, fieldId, itemId);
      delete this.cache[this.getItemKey(taxonomyId, fieldId, itemId)];

      if (this.range.rowCount == 2 /* header + one row */) {
        this.containsData = false;
      } else {
        this.range.rowCount--;
      }

      return true;
    }

    return false;
  }

  getItems(taxonomyId: number, fieldId: number, itemId: string): DropDownItem[] {
    const key = this.getItemKey(taxonomyId, fieldId, itemId);
    const items = this.cache[key];

    return items ? [...items] : undefined;
  }

  getRowPosition(taxonomyId: number, fieldId: number, itemId: string): number {
    if (this.exists(taxonomyId, fieldId, itemId)) {
      return Object
        .keys(this.cache)
        .indexOf(this.getItemKey(taxonomyId, fieldId, itemId));
    }

    return -1;
  }

  getRange(taxonomyId: number, fieldId: number, itemId: string): Range {
    if (this.exists(taxonomyId, fieldId, itemId)) {
      return new Range(
        this.range.rowIndex + this.getRowPosition(taxonomyId, fieldId, itemId) + 1, // header
        this.range.columnIndex + this.permanentColumnsCount,
        1,
        this.cache[this.getItemKey(taxonomyId, fieldId, itemId)].length,
        this.range.sheetName);
    }

    return undefined;
  }

  getFieldItemIds(taxonomyId: number, fieldId: number): string[] {
    const key = this.getFieldKey(taxonomyId, fieldId);
    const cachedItemIds = this.taxonomyFieldItemIdsCache[key];

    return cachedItemIds ? [...cachedItemIds] : undefined;
  }

  getItemId(taxonomyId: number, fieldId: number, items: DropDownItem[]): string {
    const key = this.getFieldKey(taxonomyId, fieldId);
    const cachedItemIds = this.taxonomyFieldItemIdsCache[key];

    if (cachedItemIds) {
      for (const itemId of cachedItemIds) {
        const itemKey = this.getItemKey(taxonomyId, fieldId, itemId);
        const cachedItems = this.cache[itemKey];

        if (cachedItems.length === items.length && cachedItems.every(ci => this.appHelper.find(items, i => i.name === ci.name))) {
          return itemId;
        }
      }
    }

    return undefined;
  }

  exists(taxonomyId: number, fieldId: number, itemId: string): boolean {
    return this.cache.hasOwnProperty(this.getItemKey(taxonomyId, fieldId, itemId));
  }

  itemsExist(taxonomyId: number, fieldId: number, items: DropDownItem[]): boolean {
    const key = this.getFieldKey(taxonomyId, fieldId);
    const cachedItemIds = this.taxonomyFieldItemIdsCache[key];

    if (cachedItemIds) {
      for (const itemId of cachedItemIds) {
        const itemKey = this.getItemKey(taxonomyId, fieldId, itemId);
        const cachedItems = this.cache[itemKey];

        if (cachedItems.length === items.length && cachedItems.every(ci => this.appHelper.find(items, i => i.name === ci.name))) {
          return true;
        }
      }
    }

    return false;
  }

  private addTaxonomyFieldItemId(taxonomyId: number, fieldId: number, itemId: string): void {
    const key = this.getFieldKey(taxonomyId, fieldId);
    const cachedItemIds = this.taxonomyFieldItemIdsCache[key];

    if (!cachedItemIds) {
      this.taxonomyFieldItemIdsCache[key] = [itemId];
    } else if (cachedItemIds.indexOf(itemId) < 0) {
      cachedItemIds.push(itemId);
    }
  }

  private removeTaxonomyFieldItemId(taxonomyId: number, fieldId: number, itemId: string): void {
    const key = this.getFieldKey(taxonomyId, fieldId);
    const cachedItemIds = this.taxonomyFieldItemIdsCache[key];

    if (cachedItemIds) {
      const index = cachedItemIds.indexOf(itemId);

      if (index >= 0) {
        cachedItemIds.splice(index, 1);

        if (cachedItemIds.length === 0) {
          delete this.taxonomyFieldItemIdsCache[key];
        }
      }
    }
  }

  private getFieldKey(taxonomyId: number, fieldId: number): string {
    return [taxonomyId, fieldId].join('|');
  }

  private getItemKey(taxonomyId: number, fieldId: number, itemId: string): string {
    return [taxonomyId, fieldId, itemId].join('|');
  }
};
